<script>
import ACLDialog from '@/components/auth/ACLDialog'
import PromptDialog from '@/components/dialogs/PromptDialog.vue'
import SaveDialog from '@/components/dialogs/SaveDialog.vue'
import OptionsDropdown from '@/components/OptionsDropdown.vue'
import StdizerAppearanceSettings from '@/components/stdizer/StdizerAppearanceSettings.vue'
import CurateDialog from '@/components/stdizer/CurateDialog'
import { defaultVTooltipProps } from '@/utils/defaultProps'

export default {
  components: {
    ACLDialog,
    CurateDialog,
    OptionsDropdown,
    PromptDialog,
    SaveDialog,
    StdizerAppearanceSettings,
  },

  props: {
    controller: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      defaultVTooltipProps,
    }
  },
}
</script>

<template>
  <div class="stdizer-protocol-toolbar d-flex gap-2 align-center">
    <v-tooltip
      top
      dense
      v-bind="defaultVTooltipProps"
      :open-on-focus="false"
      :open-on-click="false"
    >
      <template #activator="{ on }">
        <v-text-field
          v-on="on"
          v-model="controller.search"
          :disabled="disabled"
          hide-details
          dense
          class="grow"
          style="max-width: 10rem"
        >
          <template #label>
            <v-icon
              small
              left
            >
              mdi-magnify
            </v-icon>
            <span class="text-muted font-weight-light small-caption">Search chemical</span>
          </template>

          <template #append>
            <v-icon
              v-if="controller.search"
              small
              class="cursor-pointer"
              @click="controller.search = ''"
            >
              mdi-close-circle
            </v-icon>
          </template>
        </v-text-field>
      </template>

      {{ `Search in fields (DTXCID, DTXSID, Name, CASRN, InChI and InChIKey)` }}
    </v-tooltip>

    <b-form-select
      v-model="controller.issue"
      :disabled="disabled || controller.issuesLoading"
      title="Filter by Issue"
      :options="controller.issues"
      id="stndizer-issue"
      style="min-width: 5rem; max-width: 10rem"
    />
    <v-tooltip
      top
      v-bind="defaultVTooltipProps"
      activator="#stndizer-issue"
    >
      Filter by Issue
    </v-tooltip>

    <b-form-select
      v-model="controller.change"
      :disabled="disabled || controller.changesLoading"
      title="Filter by Change"
      :options="controller.changes"
      id="stndizer-change"
      style="min-width: 5em; max-width: 10rem"
    />
    <v-tooltip
      top
      v-bind="defaultVTooltipProps"
      activator="#stndizer-change"
    >
      Filter by Change
    </v-tooltip>

    <StdizerAppearanceSettings
      :controller="controller"
      :disabled="disabled"
    />

    <template v-if="controller.curateMode && controller.statuses.length">
      <v-checkbox
        v-for="status in controller.statuses"
        :key="status.value"
        v-model="controller.curationFilter"
        :value="status.value"
        :title="status.text"
        :disabled="disabled"
        class="mr-3 mt-5"
        @change="controller.statusFilterChanged"
      >
        <template #label>
          <span class="mt-2">
            <v-icon
              v-if="status.value === 'CORRECT'"
              :color="status.color"
              title="Correct"
              v-b-tooltip
            >
              mdi-thumb-up-outline
            </v-icon>
            <v-icon
              v-else-if="status.value === 'QUESTIONABLE'"
              :color="status.color"
              title="Questionable"
              v-b-tooltip
            >
              mdi-head-question-outline
            </v-icon>
            <v-icon
              v-else-if="status.value === 'INCORRECT'"
              :color="status.color"
              title="Incorrect"
              v-b-tooltip
            >
              mdi-thumb-down-outline
            </v-icon>
            <v-icon
              v-else
              title="Unmarked"
              v-b-tooltip
            >
              mdi-circle-off-outline
            </v-icon>
          </span>
        </template>
      </v-checkbox>
    </template>

    <v-switch
      v-if="controller.isAuthenticated"
      v-model="controller.curateMode"
      :disabled="disabled"
      class="mt-5 mr-4"
      label="Curate"
      title="Show/hide curation tools"
    />

    <v-switch
      v-if="controller.isAuthenticated && controller.curateMode"
      v-model="controller.selectable"
      :disabled="disabled"
      class="mt-5 mr-4"
      label="Select"
      title="Group curation"
    />

    <v-btn-toggle
      v-if="controller.protocol && controller.selectable"
      dense
    >
      <CurateDialog
        v-model="controller.visibleCurate"
        :visible="controller.selectable"
        :disabled="disabled || controller.selected.length === 0"
        :items="controller.selected"
        :protocol="controller.protocol"
        @updated="controller.updateItems"
      />
    </v-btn-toggle>

    <OptionsDropdown
      :items="controller.stdizerExportItems"
      :loading="controller.downloading"
      title="Export to SDF, Excel, Word or PDF"
      :disabled="disabled || !controller.filteredItems.length"
      class="flat-button"
    />
    <v-btn-toggle
      v-if="
        controller.protocol &&
        (controller.isAuthenticated ||
          (controller.protocol.id && controller.canACL(controller.protocol)) ||
          controller.canDelete(controller.protocol))
      "
      dense
    >
      <SaveDialog
        v-if="controller.isAuthenticated"
        :api-url="`${controller.STDIZER_URL}/protocols`"
        :disabled="disabled"
        :button-title="controller.protocol.id ? 'mdi-content-save-edit-outline' : 'mdi-content-save-outline'"
        :item="controller.protocol"
        :title="controller.protocol.id ? `Edit '${controller.protocol.name}' protocol` : 'Save protocol'"
        @itemCreated="controller.onProtocolCreated"
        @itemUpdated="controller.onProtocolUpdated"
      />

      <ACLDialog
        v-if="controller.protocol.id && controller.canACL(controller.protocol)"
        :item="controller.protocol"
        type="protocols"
        :api-url="controller.STDIZER_URL"
        button-title="mdi-account-key"
        title="Access Control"
        @aclUpdated="controller.onAclUpdated"
      />

      <PromptDialog
        v-if="controller.canDelete(controller.protocol)"
        button-title="mdi-delete-outline"
        :text="
          controller.protocol.id ? `Delete '${controller.protocol.name}' protocol` : 'Do you want to clear protocol?'
        "
        :title="controller.protocol.id ? `Delete '${controller.protocol.name}' protocol` : 'Clear protocol'"
        @confirmed="controller.onReset"
      />
    </v-btn-toggle>
  </div>
</template>

<style scoped>
.stdizer-protocol-toolbar {
  min-width: 0;
  flex-wrap: nowrap;
}
</style>
